import React, { useState, useMemo } from 'react';
import { Form, Button, Typography, message, Space, Progress } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  KeyOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Login.css';
import './ChangePassword.css';

const { Text } = Typography;

const fieldBox = {
  display: 'flex',
  alignItems: 'center',
  background: 'rgba(255,255,255,0.10)',
  border: '1px solid rgba(255,255,255,0.22)',
  borderRadius: '10px',
  height: '52px',
  padding: '0 14px',
  gap: '10px',
};

const nativeInput = {
  flex: 1,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'white',
  fontSize: '15px',
  lineHeight: '52px',
};

const iconStyle = { color: 'rgba(255,255,255,0.45)', fontSize: '16px', flexShrink: 0 };

// Password policy requirements (must match backend)
const PASSWORD_POLICY = {
  minLength: 10,
  requireUppercase: true,
  requireLowercase: true,
  requireDigit: true,
  requireSpecial: true,
  allowedSpecialChars: "!@#$%^&*()_+-={}[]:;\"'<>,.?/",
};

function validatePassword(password) {
  const checks = {
    length: password.length >= PASSWORD_POLICY.minLength,
    uppercase: !PASSWORD_POLICY.requireUppercase || /[A-Z]/.test(password),
    lowercase: !PASSWORD_POLICY.requireLowercase || /[a-z]/.test(password),
    digit: !PASSWORD_POLICY.requireDigit || /\d/.test(password),
    special: !PASSWORD_POLICY.requireSpecial || new RegExp(`[${PASSWORD_POLICY.allowedSpecialChars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`).test(password),
    noSpaces: !/\s/.test(password),
  };

  const isValid = Object.values(checks).every(v => v);
  const strength = Object.values(checks).filter(v => v).length / Object.keys(checks).length;

  return { checks, isValid, strength };
}

const CustomPassword = React.forwardRef(({ value, onChange, placeholder, autoComplete }, ref) => {
  const [visible, setVisible] = useState(false);
  return (
    <div style={fieldBox}>
      <LockOutlined style={iconStyle} />
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        style={nativeInput}
        autoComplete={autoComplete}
        className="login-native-input"
      />
      <span
        style={{ ...iconStyle, cursor: 'pointer' }}
        onClick={() => setVisible(v => !v)}
      >
        {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
      </span>
    </div>
  );
});
CustomPassword.displayName = 'CustomPassword';

function ChangePassword() {
  const { user, setUserState } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const newPassword = Form.useWatch('new_password', form);
  const { checks, isValid, strength } = useMemo(() => validatePassword(newPassword || ''), [newPassword]);

  const onFinish = async (values) => {
    if (values.new_password !== values.confirm_password) {
      message.error('Le password non coincidono');
      return;
    }

    if (!isValid) {
      message.error('Password non conforme ai requisiti');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/users/${user.id}/change-password`, {
        old_password: values.old_password,
        new_password: values.new_password,
      });
      // Update local user state
      const updatedUser = { ...user, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUserState(updatedUser);
      message.success('Password aggiornata con successo');
      window.location.href = '/dashboard';
    } catch (error) {
      const detail = error.response?.data?.detail || 'Errore durante l\'aggiornamento della password';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page change-password-page">
      <div className="login-bg">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />
      </div>

      <div className="login-left">
        <div className="login-brand">
          <img src="/logo_nke_login.png" alt="NKE Sterling" className="login-logo-nke" />
          <img src="/logo_app.png?v=2" alt="Arkivio QHSE" className="login-logo-app" />
          <p className="login-tagline">
            Proteggi il tuo accesso<br />
            <span>Imposta una password personale prima di continuare</span>
          </p>
        </div>
        <div className="login-features">
          <div className="feature-item"><span className="feature-dot" />Password temporanea sostituita subito</div>
          <div className="feature-item"><span className="feature-dot" />Requisiti di sicurezza verificati in tempo reale</div>
          <div className="feature-item"><span className="feature-dot" />Accesso alla piattaforma dopo il salvataggio</div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-card change-password-card">
          <div className="login-card-header">
            <div className="login-icon-box">
              <SafetyCertificateOutlined />
            </div>
            <h2>Cambio password obbligatorio</h2>
            <Text>
              Per continuare devi scegliere una nuova password personale
            </Text>
          </div>

          <Form
            name="change-password"
            form={form}
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              label="Password attuale"
              name="old_password"
              rules={[{ required: true, message: 'Inserisci la password attuale' }]}
            >
              <CustomPassword placeholder="Password attuale" autoComplete="current-password" />
            </Form.Item>

            <Form.Item
              label="Nuova password"
              name="new_password"
              rules={[{ required: true, message: 'Inserisci la nuova password' }]}
            >
              <CustomPassword placeholder="Nuova password" autoComplete="new-password" />
            </Form.Item>

            {newPassword && (
              <div className="change-password-strength">
                <div className="change-password-strength-header">
                  <Text>Complessità password</Text>
                  <Text>{Math.round(strength * 100)}%</Text>
                </div>
                <Progress
                  percent={Math.round(strength * 100)}
                  strokeColor={strength < 0.5 ? '#ff7875' : strength < 0.8 ? '#faad14' : '#52c41a'}
                  trailColor="rgba(255,255,255,0.16)"
                  size="small"
                  showInfo={false}
                />
                <div className="password-requirements">
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Requirement
                      met={checks.length}
                      label={`Almeno ${PASSWORD_POLICY.minLength} caratteri`}
                    />
                    <Requirement
                      met={checks.uppercase}
                      label="Almeno una maiuscola (A-Z)"
                    />
                    <Requirement
                      met={checks.lowercase}
                      label="Almeno una minuscola (a-z)"
                    />
                    <Requirement
                      met={checks.digit}
                      label="Almeno una cifra (0-9)"
                    />
                    <Requirement
                      met={checks.special}
                      label="Almeno un carattere speciale"
                    />
                    <Requirement
                      met={checks.noSpaces}
                      label="Senza spazi"
                    />
                  </Space>
                </div>
              </div>
            )}

            <Form.Item
              label="Conferma nuova password"
              name="confirm_password"
              dependencies={['new_password']}
              rules={[
                { required: true, message: 'Conferma la nuova password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Le password non coincidono'));
                  },
                }),
              ]}
            >
              <CustomPassword placeholder="Conferma nuova password" autoComplete="new-password" />
            </Form.Item>

            <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                disabled={!isValid && !!newPassword}
                icon={<KeyOutlined />}
                iconPosition="end"
                style={{
                  height: '52px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #1565c0, #1e88e5)',
                  border: 'none',
                  fontSize: '15px',
                  fontWeight: 600,
                  boxShadow: '0 4px 20px rgba(21,101,192,0.45)',
                }}
              >
                Aggiorna password
              </Button>
            </Form.Item>
          </Form>

          <div className="login-footer">
            <Text>
              © {new Date().getFullYear()} NKE Sterling - Arkivio
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

function Requirement({ met, label }) {
  return (
    <div className={`requirement ${met ? 'met' : 'unmet'}`}>
      {met ? (
        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
      ) : (
        <CloseCircleOutlined style={{ color: '#d9d9d9', marginRight: 8 }} />
      )}
      <Text>
        {label}
      </Text>
    </div>
  );
}

export default ChangePassword;
